import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Spinner, Banner, TextField, Checkbox, Button, InlineStack, Text } from '@shopify/polaris';
import { useAuthFetch } from './AuthComponent';

function buildInitialEditState(variants) {
    // Per-variant edit state: { stocked: bool, on_hand: number, confirming: bool }
    const init = {};
    (variants || []).forEach(v => {
        init[v.id] = {
            stocked:   v.stocked,
            on_hand:   v.on_hand || 0,
            confirming: false,
        };
    });
    return init;
}

function diffMutations(initialVariants, edit) {
    /* Returns mutations array for /stock-mutations. Three rules:
       - Untracked variants support the per-location stocked flag (independent
         of inventory tracking) but never carry an on_hand.
       - On API 2026-04+ the backend uses inventoryBulkToggleActivation,
         which PRESERVES the deactivated level's on_hand when reactivating.
         So activating a variant that has preserved on_hand (was deactivated
         with a non-zero on_hand) needs NO explicit qty — let Shopify restore.
       - Only pass on_hand on activate when the user actively MODIFIED the
         qty field during this edit session (isQty !== wasQty). Otherwise
         the value displayed in the field is the preserved historical we
         pulled from Shopify; firing setQuantities with the same number is
         redundant + opens an extra mutation call. */
    const mutations = [];
    (initialVariants || []).forEach(v => {
        const e = edit[v.id];
        if (!e) return;
        const wasStocked = v.stocked, isStocked = e.stocked;
        const wasQty = v.on_hand || 0, isQty = e.on_hand || 0;
        if (!wasStocked && isStocked) {
            const mut = { variant_id: v.id, action: 'activate' };
            // Only seed on_hand when the user explicitly changed the qty
            // from what Shopify had preserved. Equal values mean "keep what
            // Shopify has" (which our backend handles natively post-2026-04).
            if (v.tracked && isQty !== wasQty) mut.on_hand = isQty;
            mutations.push(mut);
        } else if (wasStocked && !isStocked) {
            mutations.push({ variant_id: v.id, action: 'deactivate' });
        } else if (wasStocked && isStocked && v.tracked && wasQty !== isQty) {
            mutations.push({ variant_id: v.id, action: 'adjust', on_hand: isQty });
        }
    });
    return mutations;
}

export default function StockProductModal({ productId, open, onClose, onSaved }) {
    const storeFetch = useAuthFetch();
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    const [product, setProduct] = useState(null);
    const [edit, setEdit]       = useState({});

    useEffect(() => {
        if (!open || !productId) return;
        setLoading(true); setError(null); setProduct(null); setEdit({});
        storeFetch(`/api/products/${encodeURIComponent(productId)}/stock-mutations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mutations: [] }),
        })
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(data => { setProduct(data); setEdit(buildInitialEditState(data.variants)); })
            .catch(err => setError(err.message || 'Load failed'))
            .finally(() => setLoading(false));
    }, [open, productId]); // eslint-disable-line

    const mutations = useMemo(() =>
        product ? diffMutations(product.variants, edit) : [],
        [product, edit]);

    const toggleStocked = (v) => {
        const e = edit[v.id] || {};
        // Toggling OFF a stocked variant with on-hand > 0 needs explicit confirm.
        if (e.stocked && (e.on_hand || 0) > 0) {
            setEdit(s => ({ ...s, [v.id]: { ...s[v.id], confirming: true } }));
            return;
        }
        // When going unstocked → stocked, default the qty field to the
        // variant's preserved on_hand (v.on_hand from Shopify, surfaced via
        // includeInactive + isActive at API 2026-04+). Pre-fix this was
        // hard-coded to 0, which hid the preserved value behind a zero in
        // the editable field until the user re-typed it.
        setEdit(s => ({ ...s, [v.id]: { ...s[v.id], stocked: !s[v.id].stocked,
            on_hand: s[v.id].stocked ? s[v.id].on_hand : (v.on_hand || 0),
            confirming: false } }));
    };

    const confirmUnstock = (v) => {
        setEdit(s => ({ ...s, [v.id]: { ...s[v.id], stocked: false, on_hand: 0, confirming: false } }));
    };

    const cancelConfirm = (v) => {
        setEdit(s => ({ ...s, [v.id]: { ...s[v.id], confirming: false } }));
    };

    const setQty = (v, raw) => {
        const n = Math.max(0, parseInt(raw || '0', 10) || 0);
        setEdit(s => ({ ...s, [v.id]: { ...s[v.id], on_hand: n } }));
    };

    const dirty = mutations.length > 0;

    const [bulkUnstockPending, setBulkUnstockPending] = useState(false);
    const [saving, setSaving]     = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [partialFailures, setPartialFailures] = useState(null);

    const stockAll = () => {
        if (!product) return;
        // For each currently-unstocked variant, default its qty to the
        // preserved historical on_hand (v.on_hand, surfaced via
        // includeInactive at API 2026-04+). diffMutations will then see
        // wasQty === isQty and skip the redundant setQuantities call —
        // pure inventoryBulkToggleActivation, which Shopify uses to restore
        // the preserved quantity naturally.
        setEdit(s => {
            const next = { ...s };
            (product.variants || []).forEach(v => {
                if (next[v.id]?.stocked) return; // already stocked, keep qty as-is
                next[v.id] = { ...next[v.id], stocked: true,
                                on_hand: v.tracked ? (v.on_hand || 0) : 0,
                                confirming: false };
            });
            return next;
        });
    };

    const unstockAllRequest = () => {
        if (!product) return;
        // If any stocked variant has on-hand > 0, surface the combined confirm.
        const at_risk = (product.variants || []).filter(v => {
            const e = edit[v.id] || {};
            return v.tracked && e.stocked && (e.on_hand || 0) > 0;
        });
        if (at_risk.length === 0) {
            unstockAllApply(); return;
        }
        setBulkUnstockPending(true);
    };

    const unstockAllApply = () => {
        setEdit(s => {
            const next = { ...s };
            (product.variants || []).forEach(v => {
                next[v.id] = { ...next[v.id], stocked: false, on_hand: 0, confirming: false };
            });
            return next;
        });
        setBulkUnstockPending(false);
    };

    const at_risk_total = (product?.variants || []).reduce((sum, v) => {
        const e = edit[v.id] || {};
        return v.tracked && e.stocked ? sum + (e.on_hand || 0) : sum;
    }, 0);
    const at_risk_count = (product?.variants || []).reduce((n, v) => {
        const e = edit[v.id] || {};
        return v.tracked && e.stocked && (e.on_hand || 0) > 0 ? n + 1 : n;
    }, 0);

    const handleSave = async () => {
        if (!product || mutations.length === 0) return;
        setSaving(true); setSaveError(null); setPartialFailures(null);
        try {
            const res = await storeFetch(`/api/products/${encodeURIComponent(productId)}/stock-mutations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mutations }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const failed = (data.results || []).filter(r => !r.ok);
            if (failed.length > 0) {
                setPartialFailures(failed);
                // Reset product state to what the server returned so the failed
                // rows show their actual current state, not the edit state.
                setProduct(data);
                setEdit(buildInitialEditState(data.variants));
                setSaving(false);
                return;
            }
            // All success → close modal + notify parent.
            // Set saving=false BEFORE onSaved — parent will close the modal,
            // unmounting this component; calling setState after unmount triggers
            // React 18 warnings.
            setSaving(false);
            onSaved?.(data);
        } catch (err) {
            setSaveError(err.message || 'Save failed');
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title="Stock product"
            primaryAction={{ content: 'Save', disabled: !dirty || saving,
                             loading: saving, onAction: handleSave }}
            secondaryActions={[{ content: 'Cancel', onAction: onClose }]}>
            <Modal.Section>
                {saveError && <Banner tone="critical" onDismiss={() => setSaveError(null)}>{saveError}</Banner>}
                {partialFailures && (
                    <Banner tone="warning" onDismiss={() => setPartialFailures(null)}>
                        Some variants failed: {partialFailures.map(f => f.variant_id.split('/').pop()).join(', ')}.
                        Successful changes were saved; failed rows are unchanged.
                    </Banner>
                )}
                {loading && <div style={{ textAlign: 'center', padding: 32 }}><Spinner /></div>}
                {error && <Banner tone="critical">{error}</Banner>}
                {product && (
                    <>
                        <div className="stock-modal__header">
                            {(product.images && product.images.length > 0) && (
                                <div className="stock-modal__images">
                                    {product.images.slice(0, 2).map((img, i) => (
                                        <img key={i} src={img.url} alt={img.altText || ''}
                                            className="stock-modal__image" />
                                    ))}
                                </div>
                            )}
                            <div className="stock-modal__header-text">
                                <div className="stock-modal__title">{product.title || '—'}</div>
                                {(product.vendor || product.product_type) && (
                                    <div className="stock-modal__meta">
                                        {product.vendor && <span>{product.vendor}</span>}
                                        {product.vendor && product.product_type && <span className="stock-modal__meta-sep">·</span>}
                                        {product.product_type && <span>{product.product_type}</span>}
                                    </div>
                                )}
                                <div className="stock-modal__subtitle">
                                    Currently <strong>{product.variants_active_at_loc}</strong> of <strong>{product.variant_count}</strong> variants stocked
                                </div>
                            </div>
                        </div>
                        {product.description_html && (
                            <div className="stock-modal__description"
                                dangerouslySetInnerHTML={{ __html: product.description_html }} />
                        )}
                        <div className="stock-modal__bulk">
                            <Button onClick={stockAll}>Stock all variants</Button>
                            <Button onClick={unstockAllRequest} tone="critical" variant="primary">Unstock all variants</Button>
                            {bulkUnstockPending && (
                                <div className="stock-modal__confirm">
                                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                                        <Text as="span" tone="caution">
                                            This will zero out {at_risk_total} units across {at_risk_count} variants.
                                        </Text>
                                        <Button size="micro" tone="critical" variant="primary"
                                            onClick={unstockAllApply}>Confirm</Button>
                                        <Button size="micro"
                                            onClick={() => setBulkUnstockPending(false)}>Cancel</Button>
                                    </InlineStack>
                                </div>
                            )}
                        </div>
                        <table className="stock-modal__variants">
                            <thead>
                                <tr>
                                    <th>Variant</th>
                                    <th>SKU</th>
                                    <th>Stocked</th>
                                    <th>On hand</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(product.variants || []).map(v => {
                                    const e = edit[v.id] || {};
                                    return (
                                        <tr key={v.id} className={!v.tracked ? 'stock-modal__row--untracked' : undefined}>
                                            <td>{v.title || '—'}</td>
                                            <td>{v.sku || '—'}</td>
                                            <td>
                                                <Checkbox checked={!!e.stocked}
                                                    onChange={() => toggleStocked(v)}
                                                    label="" />
                                            </td>
                                            <td>
                                                {!v.tracked ? (
                                                    <span style={{ color: '#6d7175', fontSize: '0.85em' }}>
                                                        Not tracked
                                                    </span>
                                                ) : e.confirming ? (
                                                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                                                        <Text as="span" tone="caution">
                                                            Will zero out {e.on_hand} units.
                                                        </Text>
                                                        <Button size="micro" tone="critical" variant="primary"
                                                            onClick={() => confirmUnstock(v)}>Confirm</Button>
                                                        <Button size="micro"
                                                            onClick={() => cancelConfirm(v)}>Cancel</Button>
                                                    </InlineStack>
                                                ) : e.stocked
                                                    ? <TextField
                                                        type="number" min={0}
                                                        value={String(e.on_hand ?? 0)}
                                                        onChange={(val) => setQty(v, val)}
                                                        autoComplete="off"
                                                        label="" labelHidden />
                                                    : (v.on_hand > 0
                                                        ? <span style={{ color: '#6d7175' }}
                                                                title="Preserved at this location from a prior activation. Check 'Stocked' to make it available again.">
                                                              {v.on_hand}
                                                          </span>
                                                        : <span style={{ color: '#9ca3af' }}>—</span>)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </>
                )}
            </Modal.Section>
        </Modal>
    );
}
